import { useEffect, useState } from "react";
import {
  createPushSubscription,
  deletePushSubscription,
  getVapidPublicKey,
} from "../lib/api";

/** Web-push browser support. iOS Safari only exposes PushManager inside an
 *  installed (home-screen) PWA — the bell hides itself elsewhere. */
function pushSupported(): boolean {
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/** Decode a base64url VAPID key into the bytes subscribe() wants. */
function urlBase64ToUint8Array(base64Url: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64Url.length % 4)) % 4);
  const base64 = (base64Url + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** Dashboard-header bell — opt in/out of web push (phase 4 §B.2).
 *  Enable: permission → service worker → pushManager.subscribe(VAPID) →
 *  POST /push/subscriptions. Disable: unsubscribe + DELETE by endpoint. */
export function PushBell() {
  const [supported] = useState(pushSupported);
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  // Reflect an existing subscription (e.g. enabled in a previous visit).
  useEffect(() => {
    if (!supported) return;
    let cancelled = false;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        const sub = await reg?.pushManager.getSubscription();
        if (!cancelled && sub && Notification.permission === "granted") {
          setOn(true);
        }
      } catch {
        // stay off — toggling will surface any real error
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [supported]);

  const enable = async () => {
    const perm = await Notification.requestPermission();
    if (perm !== "granted") {
      setNote("notifications blocked — allow them in browser settings");
      return;
    }
    await navigator.serviceWorker.register("/sw.js");
    const reg = await navigator.serviceWorker.ready;
    const key = await getVapidPublicKey();
    const sub =
      (await reg.pushManager.getSubscription()) ??
      (await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(key),
      }));
    await createPushSubscription(sub.toJSON());
    setOn(true);
  };

  const disable = async () => {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = await reg?.pushManager.getSubscription();
    if (sub) {
      const endpoint = sub.endpoint;
      await sub.unsubscribe();
      await deletePushSubscription(endpoint);
    }
    setOn(false);
  };

  const toggle = async () => {
    if (busy) return;
    setBusy(true);
    setNote(null);
    try {
      if (on) await disable();
      else await enable();
    } catch (e) {
      setNote(e instanceof Error ? e.message : "push setup failed");
    } finally {
      setBusy(false);
    }
  };

  if (!supported) return null;

  return (
    <>
      <button
        type="button"
        className={`dsh-iconbtn${on ? " on" : ""}`}
        onClick={toggle}
        disabled={busy}
        aria-pressed={on}
        aria-label={
          on
            ? "Push notifications on — click to disable"
            : "Enable push notifications"
        }
        title={
          on
            ? "Push notifications on — click to disable"
            : "Enable push notifications"
        }
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {on && <span className="dsh-bell-dot" aria-hidden="true" />}
      </button>
      {note && <span className="dsh-bell-note">{note}</span>}
    </>
  );
}
